import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Plus, Wallet, TrendingUp, Clock, Search, X, Loader2, Trash2, CheckCircle2,
  Paperclip, ExternalLink, FileText, Filter,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ContabilitaUtile from "@/components/ContabilitaUtile";

import { eur } from "@/lib/format";
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const meseLabel = (ym) => { const [y, m] = (ym || "").split("-"); return m ? `${MESI[+m - 1]} ${y}` : ym; };
const CHART_MARGIN = { top: 8, right: 8, left: -12, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#5A5A5A" };
const LEGEND_STYLE = { fontSize: 12 };
const PIE_COLORS = ["#1B2A4B", "#A8863F", "#2E5E3A", "#8A2E2E", "#5A5A5A", "#3b6ea5", "#6B5523", "#9A3B13"];

export default function Contabilita() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canManage = can("gestione_contabilita");
  const [filters, setFilters] = useState({ tipo: "", tipo_pratica: "", da: "", a: "" });
  const [open, setOpen] = useState(false);

  const { data: pratiche } = useQuery({ queryKey: ["pratiche"], queryFn: async () => (await api.get("/pratiche")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["contabilita", filters],
    queryFn: async () => (await api.get("/contabilita", { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) })).data,
  });

  const movimenti = data?.movimenti || [];
  const stats = data?.stats || { totale_incassato: 0, totale_promesso: 0, per_mese: [], per_tipo: [] };
  const tipiPratica = useMemo(() => [...new Set((pratiche || []).map((p) => p.tipo_pratica_nome))].sort(), [pratiche]);
  const perMese = (stats.per_mese || []).map((m) => ({ ...m, label: meseLabel(m.mese) }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["contabilita"] });

  const incassa = async (m) => {
    try { await api.post(`/contabilita/${m.id}/incassa`); toast.success("Pagamento segnato come incassato"); invalidate(); qc.invalidateQueries({ queryKey: ["pratiche"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Eliminare il movimento di ${eur(m.importo)}?`)) return;
    try { await api.delete(`/contabilita/${m.id}`); toast.success("Movimento eliminato"); invalidate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  return (
    <div data-testid="contabilita-page" id="contabilita"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex flex-col gap-0.5">
            <h1 className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B]">Contabilità</h1>
            <p className="text-[14px] md:text-[16px] text-[#4A4A48]">
              Registro dei pagamenti dello studio · {movimenti.length} movimenti nel periodo
            </p>
          </div>
          {canManage && (
            <button data-testid="new-movimento-btn" onClick={() => setOpen(true)} className="btn btn-g btn-larghezza sm:w-auto">
              <Plus className="w-4 h-4" /> Registra pagamento
            </button>
          )}
        </header>

        <div className="cifre" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <div className="cifra" data-testid="stat-incassato">
            <span className="eti">Totale incassato</span>
            <b className="text-[#2E5E3A]">{eur(stats.totale_incassato)}</b>
            <small>pagamenti già arrivati</small>
          </div>
          <div className="cifra" data-testid="stat-promesso" style={{ background: "#F7F6F2" }}>
            <span className="eti">Da incassare</span>
            <b className="text-[#1A1A1A]">{eur(stats.totale_promesso)}</b>
            <small>pagamenti promessi</small>
          </div>
          <div className="cifra" data-testid="stat-movimenti">
            <span className="eti">Movimenti</span>
            <b className="text-[#1B2A4B]">{movimenti.length}</b>
            <small>righe nel periodo scelto</small>
          </div>
        </div>

        <div className="sez px-4 py-3.5 flex flex-wrap items-end gap-3.5" data-testid="contabilita-filters">
          <span className="eti flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Filtri</span>
          <FField label="Tipo">
            <select data-testid="filter-tipo" value={filters.tipo} onChange={(e) => setFilters({ ...filters, tipo: e.target.value })} className="campo">
              <option value="">Tutti</option>
              <option value="incassato">Incassato</option>
              <option value="promesso">Promesso</option>
            </select>
          </FField>
          <FField label="Tipo pratica">
            <select data-testid="filter-tipo-pratica" value={filters.tipo_pratica} onChange={(e) => setFilters({ ...filters, tipo_pratica: e.target.value })} className="campo">
              <option value="">Tutte</option>
              {tipiPratica.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FField>
          <FField label="Dal"><input data-testid="filter-da" type="date" value={filters.da} onChange={(e) => setFilters({ ...filters, da: e.target.value })} className="campo" /></FField>
          <FField label="Al"><input data-testid="filter-a" type="date" value={filters.a} onChange={(e) => setFilters({ ...filters, a: e.target.value })} className="campo" /></FField>
          {(filters.tipo || filters.tipo_pratica || filters.da || filters.a) && (
            <button data-testid="filter-reset" onClick={() => setFilters({ tipo: "", tipo_pratica: "", da: "", a: "" })}
              className="text-[13px] font-semibold text-[#8A6E2F] hover:text-[#8A2E2E]">Azzera filtri</button>
          )}
        </div>

        <ContabilitaUtile filters={filters} />

        <div className="sez overflow-hidden" data-testid="movimenti-elenco">
          <div className="subt">
            <span>Movimenti</span>
            <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">{movimenti.length} righe</span>
          </div>
          {isLoading && <div className="riga text-[#5A5A5A]"><Loader2 className="w-4 h-4 animate-spin" /> Carico i movimenti…</div>}
          {!isLoading && movimenti.length === 0 && <div className="riga text-[#5A5A5A]">Nessun movimento nel periodo scelto.</div>}
          {movimenti.map((m) => <MovimentoRow key={m.id} m={m} canManage={canManage} onIncassa={incassa} onRemove={remove} onChanged={invalidate} />)}
        </div>

        <div className="grid lg:grid-cols-2 gap-4 min-w-0">
          <div className="sez p-4 min-w-0" data-testid="chart-mensile">
            <div className="eti mb-3">Andamento mensile</div>
            {perMese.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={perMese} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0EEE8" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Bar dataKey="incassato" name="Incassato" fill="#2E5E3A" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="promesso" name="Promesso" fill="#A8863F" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="sez p-4 min-w-0" data-testid="chart-tipo">
            <div className="eti mb-3">Incassato per tipo pratica</div>
            {(stats.per_tipo || []).length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={stats.per_tipo} dataKey="valore" nameKey="tipo" cx="50%" cy="50%" outerRadius={80} label={(e) => e.tipo}>
                    {stats.per_tipo.map((entry, i) => <Cell key={entry.tipo || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => eur(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ContabilitaUtile è un pannello storico: qui si allinea a carte, bordi e titoli delle altre pagine */}
      <style>{`
        #contabilita .bg-white { border-radius: 6px; }
        #contabilita [class*="border-[#1A2B4A]"] { border-color: #E2E0D9; }
      `}</style>

      {open && <MovimentoDialog pratiche={pratiche || []} onClose={() => setOpen(false)} onSaved={() => { invalidate(); qc.invalidateQueries({ queryKey: ["pratiche"] }); }} />}
    </div>
  );
}

const Empty = () => <div className="h-[240px] flex items-center justify-center text-[14px] text-[#5A5A5A]">Nessun dato da mostrare.</div>;
const FField = ({ label, children }) => (<label className="flex flex-col gap-1"><span className="eti">{label}</span>{children}</label>);

function MovimentoRow({ m, canManage, onIncassa, onRemove, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const uploadProof = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/contabilita/${m.id}/proof`, fd);
      toast.success("Prova di pagamento caricata su Drive");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload non riuscito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div data-testid="movimento-row" className="riga group gap-4 flex-wrap">
      <span className="w-[92px] shrink-0 text-[#4A4A48]">{m.data}</span>
      <span className="flex-[999_1_200px] min-w-0">
        <span className="block truncate">
          {m.cliente_id
            ? <Link to="/clienti" data-testid="movimento-cliente-link" className="font-semibold no-underline">{m.cliente_nome || "—"}</Link>
            : <span className="text-[#5A5A5A]">senza cliente</span>}
          {m.pratica_id && (
            <>
              <span className="text-[#5A5A5A]"> · </span>
              <Link to={`/pratiche/${m.pratica_id}`} data-testid="movimento-pratica-link" className="no-underline">{m.pratica_nome || "Pratica"}</Link>
            </>
          )}
        </span>
        <span className="block text-[12px] text-[#5A5A5A] truncate" title={m.nota}>
          {m.tipo === "incassato" ? "incassato" : "promesso"}{m.nota ? ` · ${m.nota}` : ""}
        </span>
      </span>
      <span className={`w-[110px] shrink-0 text-right text-[15px] font-bold ${m.tipo === "incassato" ? "text-[#2E5E3A]" : "text-[#1A1A1A]"}`}>
        {eur(m.importo)}
      </span>
      <span className="w-[80px] shrink-0 text-[13px]">
        {m.proof_link
          ? <a href={m.proof_link} target="_blank" rel="noreferrer" data-testid="movimento-proof-link" className="font-semibold no-underline">prova</a>
          : <span className="text-[#5A5A5A]">—</span>}
      </span>
      <span className="shrink-0 flex items-center gap-2.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        {canManage && m.tipo === "promesso" && (
          <button data-testid="movimento-incassa" onClick={() => onIncassa(m)} title="Segna come incassato"
            className="text-[#2E5E3A]/70 hover:text-[#2E5E3A]"><CheckCircle2 className="w-4 h-4" /></button>
        )}
        {canManage && m.cliente_id && (
          <button data-testid="movimento-proof-btn" onClick={() => fileRef.current?.click()} disabled={busy}
            title="Carica la prova del pagamento" className="text-[#5A5A5A] hover:text-[#A8863F] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </button>
        )}
        {canManage && m.origine !== "acconto" && (
          <button data-testid="movimento-delete" onClick={() => onRemove(m)} title="Elimina"
            className="text-[#8A2E2E]/60 hover:text-[#8A2E2E]"><Trash2 className="w-4 h-4" /></button>
        )}
        <input ref={fileRef} type="file" className="hidden" accept="image/*,application/pdf"
          data-testid="movimento-proof-input" onChange={(e) => uploadProof(e.target.files?.[0])} />
      </span>
    </div>
  );
}

function MovimentoDialog({ pratiche, onClose, onSaved }) {
  const { data: clienti } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data });
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ tipo: "incassato", importo: "", data: today, cliente_id: "", pratica_id: "", nota: "" });
  const [cliQuery, setCliQuery] = useState("");
  const [cliOpen, setCliOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedCli = (clienti || []).find((c) => c.id === form.cliente_id);
  const filteredCli = (clienti || []).filter((c) => `${c.nome} ${c.cognome}`.toLowerCase().includes(cliQuery.trim().toLowerCase())).slice(0, 60);
  const clientPratiche = (pratiche || []).filter((p) => p.cliente_id === form.cliente_id);

  const save = async () => {
    if (!form.importo || Number(form.importo) <= 0) return toast.error("Inserisci un importo valido");
    if (form.tipo === "promesso" && form.data <= today) toast.warning("Per un pagamento promesso conviene una data futura (il promemoria è 1 giorno prima).");
    setSaving(true);
    try {
      await api.post("/contabilita", {
        tipo: form.tipo, importo: Number(form.importo), data: form.data,
        cliente_id: form.cliente_id || null, pratica_id: form.pratica_id || null, nota: form.nota,
      });
      toast.success(form.tipo === "promesso" ? "Pagamento promesso registrato (promemoria creato)" : "Pagamento registrato");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm backdrop-blur-sm flex items-center justify-center p-4" data-testid="movimento-dialog">
      <div className="dialogo max-w-md w-full p-5 max-h-[92vh] overflow-y-auto">
        <h3 className="text-[20px] font-bold text-[#1B2A4B] mb-4">Registra pagamento</h3>

        <div className="flex gap-2 mb-4">
          {[["incassato", "Incassato"], ["promesso", "Promesso"]].map(([v, l]) => (
            <button key={v} data-testid={`mov-tipo-${v}`} onClick={() => setForm({ ...form, tipo: v })}
              className={`btn flex-1 ${form.tipo === v ? "btn-p" : "btn-o"}`}>{l}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="eti">Importo (€)</label>
            <input data-testid="mov-importo" type="number" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })}
              className="campo mt-1" />
          </div>
          <div>
            <label className="eti">{form.tipo === "promesso" ? "Data prevista" : "Data"}</label>
            <input data-testid="mov-data" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
              className="campo mt-1" />
          </div>
        </div>

        <label className="eti">Cliente</label>
        <div className="relative mt-1 mb-4">
          <div className="flex items-center campo gap-2 py-0">
            <Search className="w-4 h-4 text-[#5A5A5A] shrink-0" />
            <input data-testid="mov-cliente-search"
              value={selectedCli && !cliOpen ? `${selectedCli.nome} ${selectedCli.cognome}` : cliQuery}
              onChange={(e) => { setCliQuery(e.target.value); setCliOpen(true); if (form.cliente_id) setForm({ ...form, cliente_id: "", pratica_id: "" }); }}
              onFocus={() => setCliOpen(true)} placeholder="Cerca cliente (opzionale)…"
              className="w-full py-2.5 bg-transparent focus:outline-none text-[14px]" />
            {(selectedCli || cliQuery) && <button onClick={() => { setForm({ ...form, cliente_id: "", pratica_id: "" }); setCliQuery(""); setCliOpen(true); }} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-4 h-4" /></button>}
          </div>
          {cliOpen && (
            <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-[#E2E0D9] rounded-[5px] shadow-lg">
              {filteredCli.length === 0 && <div className="riga text-[#5A5A5A]">Nessun cliente.</div>}
              {filteredCli.map((c) => (
                <button key={c.id} data-testid={`mov-cliente-option-${c.id}`} onClick={() => { setForm({ ...form, cliente_id: c.id, pratica_id: "" }); setCliOpen(false); setCliQuery(""); }}
                  className="w-full text-left px-3.5 py-2.5 text-[14px] hover:bg-[#F7F6F2]">{c.nome} {c.cognome}</button>
              ))}
            </div>
          )}
        </div>

        {form.cliente_id && (
          <div className="mb-4">
            <label className="eti">Pratica di riferimento (opzionale)</label>
            <select data-testid="mov-pratica" value={form.pratica_id} onChange={(e) => setForm({ ...form, pratica_id: e.target.value })}
              className="campo mt-1">
              <option value="">— Nessuna —</option>
              {clientPratiche.map((p) => <option key={p.id} value={p.id}>{p.tipo_pratica_nome} · residuo {eur((p.importo_totale || 0) - (p.importo_pagato || 0))}</option>)}
            </select>
            {form.tipo === "incassato" && form.pratica_id && <p className="text-[12px] text-[#2E5E3A] mt-1">Verrà registrato anche come incasso sulla pratica.</p>}
          </div>
        )}

        <label className="eti">Annotazioni</label>
        <textarea data-testid="mov-nota" value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} rows={2}
          className="campo mt-1 mb-5" placeholder="Es. bonifico ricevuto, causale…" />

        <div className="flex justify-end gap-2.5">
          <button onClick={onClose} disabled={saving} className="btn btn-o">Annulla</button>
          <button data-testid="mov-save" onClick={save} disabled={saving} className="btn btn-p">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Registra
          </button>
        </div>
      </div>
    </div>
  );
}
